import { action, computed, observable, reaction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { ProjectsRootVisualStore } from '../../common/stores';
import { InternalSelectOption } from '../types';
import ApplicationDefinitionsService from '../services/ApplicationDefinitionsService';

export default class ProjectApplicationDefinitionsSourcesStore {
    @observable
    internalSelectOptions: Record<string, InternalSelectOption[]> = {};

    @observable
    isLoading: boolean = false;

    private cancelTokenSource: CancelTokenSource | null = null;

    @computed
    get project() {
        return this.rootStore.currentProject;
    }

    constructor(
        private rootStore: ProjectsRootVisualStore,
        private service: ApplicationDefinitionsService
    ) {
        reaction(
            () => this.project,
            () => {
                if (this.project) {
                    this.dispose();
                    this.initialize();
                }
            }
        );
    }

    @action.bound
    setInternalSelectOptions(internalSelectOptions: Record<string, InternalSelectOption[]>) {
        this.internalSelectOptions = internalSelectOptions;
    }

    @action.bound
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @action.bound
    async loadInternalSelectOptions() {
        if (!this.project) {
            return;
        }

        this.cancelRequest();
        const currentCancelTokenSource = axios.CancelToken.source();
        this.cancelTokenSource = currentCancelTokenSource;

        this.setIsLoading(true);

        try {
            const response = await this.service.getInternalSelectOptions(
                this.project.id,
                currentCancelTokenSource.token
            );

            if (this.cancelTokenSource !== currentCancelTokenSource) {
                return;
            }

            if (response.isErr()) {
                console.error('Error loading internal select options: ', response.error);
                return;
            }

            response.map(internalSelectOptions => this.setInternalSelectOptions(internalSelectOptions));
        } finally {
            if (this.cancelTokenSource === currentCancelTokenSource) {
                this.setIsLoading(false);
                this.cancelTokenSource = null;
            }
        }
    }

    initialize() {
        this.loadInternalSelectOptions();
    }

    dispose() {
        this.cancelRequest();
        this.setInternalSelectOptions({});
    }

    cancelRequest() {
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel();
            this.cancelTokenSource = null;
        }

        this.setIsLoading(false);
    }
}
